
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getSeverityColor } from '@/data/gas-analytics-data';
import { Hash } from 'lucide-react';
import { formatDeviceId, getTooltipStyle } from '@/components/telemetry/utils/chartUtils';

// Sample telemetry data
const deviceTelemetry = [
  { time: '00:00', pressure: 2.15, flow: 35 },
  { time: '02:00', pressure: 2.14, flow: 32 },
  { time: '04:00', pressure: 2.12, flow: 30 },
  { time: '06:00', pressure: 2.08, flow: 28 },
  { time: '08:00', pressure: 2.05, flow: 25 },
  { time: '10:00', pressure: 2.03, flow: 22 },
  { time: '12:00', pressure: 2.07, flow: 26 },
  { time: '14:00', pressure: 2.10, flow: 29 },
  { time: '16:00', pressure: 2.11, flow: 31 },
  { time: '18:00', pressure: 2.12, flow: 33 },
  { time: '20:00', pressure: 2.14, flow: 36 },
  { time: '22:00', pressure: 2.15, flow: 38 },
];

interface GasDeviceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: {
    id: number;
    device: string;
    issue: string;
    severity: string;
    time: string;
    generatedDeviceId?: string;
  } | null;
}

const GasDeviceDetailsModal = ({ isOpen, onClose, device }: GasDeviceDetailsModalProps) => {
  if (!device) return null;

  // Generate device ID if not present
  const deviceId = device.generatedDeviceId || `GAS-${device.id.toString().padStart(3, '0')}`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{device.device} Details</DialogTitle>
          <div className="flex items-center text-sm text-muted-foreground mt-1 mb-2">
            <Hash className="h-4 w-4 mr-1" />
            <span className="font-mono">{formatDeviceId(deviceId)}</span>
          </div>
          <DialogDescription>
            Issue: {device.issue} - 
            <span className={`font-medium ${getSeverityColor(device.severity)}`}>
              {device.severity} Severity
            </span> - {device.time}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-3">Device Telemetry - {formatDeviceId(deviceId)}</h3>
            
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={deviceTelemetry}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis yAxisId="left" domain={[2.0, 2.2]} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 40]} />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === 'pressure') return [`${value} bar`, 'Pressure'];
                      return [`${value} m³/h`, 'Flow Rate'];
                    }}
                    contentStyle={getTooltipStyle()}
                  />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="pressure" 
                    name="pressure"
                    stroke="#F97316" 
                    activeDot={{ r: 8 }}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="flow" 
                    name="flow"
                    stroke="#FBBF24" 
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-2">Device Information</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span className="font-medium">Device ID:</span> {formatDeviceId(deviceId)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span className="font-medium">Type:</span> {device.device.includes('Line') ? 'Gas Line' : 'Gas Appliance'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span className="font-medium">Location:</span> Building A, Floor 2
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span className="font-medium">Installation Date:</span> January 10, 2025
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">Last Maintenance:</span> October 10, 2024
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-2">Alarm Details</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span className="font-medium">Alarm ID:</span> {device.id}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span className="font-medium">Device ID:</span> {formatDeviceId(deviceId)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span className="font-medium">Detected:</span> {device.time}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span className="font-medium">Issue:</span> {device.issue}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">Current Status:</span> Active
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GasDeviceDetailsModal;
